import { useState, useCallback } from "react";
import useMermaidToBeaUX, { ProcessedLayout, convertLayoutToBeaUXState } from "./useMermaidToBeaUX";

// Interface for the result of processing a Repomix XML file
export interface RepomixProcessResult {
  mermaidCode: string; // Generated Mermaid representation
  layout: ProcessedLayout | null; // Processed layout data
  error: string | null; // Error message, if any
}

// Interface for a simplified filter object
export interface Filter {
  filter_type: "name_contains" | "related_to_file" | "node_type";
  value: string;
  node_types?: string[];
  depth?: number;
}

// Interface for a parsed Repomix node
interface RepomixNode {
  id: string;
  type: "dir" | "file";
  name: string;
  parent: string;
}

// Interface for a parsed Repomix edge
interface RepomixEdge {
  source: string;
  target: string;
  type: "structure" | "dependency";
}

// Interface for parsed Repomix data
interface ParsedRepomixData {
  nodes: RepomixNode[];
  edges: RepomixEdge[];
}

/**
 * Hook for processing Repomix XML files and generating beaUX diagrams
 */
const useRepomixToBeaUX = () => {
  const [parsedData, setParsedData] = useState<ParsedRepomixData | null>(null);
  const { processMermaid } = useMermaidToBeaUX();

  /**
   * Process a Repomix XML file and extract structure and dependencies
   */
  const processRepomixXml = useCallback(
    async (xmlContent: string): Promise<RepomixProcessResult> => {
      if (!xmlContent.trim()) {
        return { mermaidCode: "", layout: null, error: "XML content is empty." };
      }

      try {
        // 1. Basic validation that this is likely a Repomix XML file
        if (!xmlContent.includes("<directory_structure>")) {
          return {
            mermaidCode: "",
            layout: null,
            error: "Invalid Repomix XML: Missing directory_structure element",
          };
        }

        // 2. We have two options for MVP:
        // A. Call the Python script via backend (preferred for production)
        // B. Re-implement the basic parsing in JavaScript for MVP (we'll do this for now)
        
        // Extract directory structure from XML
        const structurePaths = extractDirectoryStructure(xmlContent);
        
        // Extract dependencies from XML
        const dependencies = extractDependencies(xmlContent, structurePaths);
        
        // Parse into nodes and edges format
        const parsedData = {
          nodes: structurePaths.map(path => {
            const parts = path.split('/');
            const name = parts[parts.length - 1];
            const parent = parts.slice(0, -1).join('/');
            const isDir = !path.includes('.'); // Simple heuristic
            
            return {
              id: path,
              type: isDir ? "dir" : "file",
              name: name,
              parent: parent
            };
          }),
          edges: [
            // Structure edges
            ...structurePaths.map(path => {
              const parts = path.split('/');
              if (parts.length <= 1) return null;
              
              const parent = parts.slice(0, -1).join('/');
              if (!parent) return null;
              
              return {
                source: parent,
                target: path,
                type: "structure" as const
              };
            }).filter(Boolean),
            
            // Dependency edges
            ...dependencies.map(([source, target]) => ({
              source,
              target,
              type: "dependency" as const
            }))
          ].filter(Boolean)
        };
        
        // Store the parsed data for later filtering
        setParsedData(parsedData);
        
        // 3. Generate simple Mermaid code for initial visualization
        const mermaidCode = generateMermaidFromParsedData(parsedData);
        
        // 4. Process the Mermaid code to get a layout
        const layout = await processMermaid(mermaidCode);
        
        return {
          mermaidCode,
          layout,
          error: null
        };
      } catch (err: any) {
        console.error("Error processing Repomix XML:", err);
        return {
          mermaidCode: "",
          layout: null,
          error: err.message || "Failed to process Repomix XML",
        };
      }
    },
    [processMermaid]
  );

  /**
   * Apply a filter to the parsed data and generate a filtered diagram
   */
  const applyFilter = useCallback(
    async (filter: Filter): Promise<RepomixProcessResult> => {
      if (!parsedData) {
        return {
          mermaidCode: "",
          layout: null,
          error: "No data to filter. Import a Repomix XML file first.",
        };
      }

      try {
        // 1. Apply the filter to get filtered nodes and edges
        const filtered = filterParsedData(parsedData, filter);
        
        // 2. Generate Mermaid code from the filtered data
        const mermaidCode = generateMermaidFromParsedData(filtered);
        
        // 3. Process the Mermaid code to get a layout
        const layout = await processMermaid(mermaidCode);
        
        return {
          mermaidCode,
          layout,
          error: null
        };
      } catch (err: any) {
        console.error("Error applying filter:", err);
        return {
          mermaidCode: "",
          layout: null,
          error: err.message || "Failed to apply filter",
        };
      }
    },
    [parsedData, processMermaid]
  );

  return {
    processRepomixXml,
    applyFilter,
    hasData: !!parsedData
  };
};

// --- Helper Functions ---

/**
 * Extract directory structure paths from Repomix XML
 */
function extractDirectoryStructure(xmlContent: string): string[] {
  const structureMatch = xmlContent.match(/<directory_structure>([\s\S]*?)<\/directory_structure>/i);
  if (!structureMatch) return [];

  const structureBlock = structureMatch[1].trim();
  const lines = structureBlock.split('\n');
  const paths = new Set<string>();
  const currentPathStack: string[] = [];

  for (const line of lines) {
    const strippedLine = line.replace(/^\s+/, '');
    if (!strippedLine) continue;

    const indentLevel = Math.floor((line.length - strippedLine.length) / 2); // Assuming 2 spaces
    
    // Pop from stack until we reach the proper parent level
    while (indentLevel < currentPathStack.length) {
      currentPathStack.pop();
    }

    const nodeName = strippedLine.replace(/\/$/, '');
    const isDir = strippedLine.endsWith('/');
    
    const parentPath = currentPathStack.join('/');
    const currentPath = parentPath ? `${parentPath}/${nodeName}` : nodeName;
    
    paths.add(currentPath);
    
    if (isDir) {
      currentPathStack.push(nodeName);
    }
  }
  
  // Add parent paths to ensure tree structure
  const finalPaths = new Set(paths);
  for (const path of paths) {
    const parts = path.split('/');
    for (let i = 1; i < parts.length; i++) {
      const parent = parts.slice(0, i).join('/');
      if (parent) finalPaths.add(parent);
    }
  }
  
  return Array.from(finalPaths).sort();
}

/**
 * Extract dependencies from Repomix XML
 */
function extractDependencies(xmlContent: string, allPaths: string[]): [string, string][] {
  const dependencies = new Set<string>();
  const pathsSet = new Set(allPaths);
  
  // Basic regex for import statements
  const importRegex = /import\s+.*?from\s+['"]([^'"]+)['"]/g;
  
  // Find file blocks in the XML
  const fileRegex = /<file path="([^"]+)">([\s\S]*?)<\/file>/gi;
  let fileMatch;
  
  while ((fileMatch = fileRegex.exec(xmlContent)) !== null) {
    const filePath = fileMatch[1].replace(/\\/g, '/');
    if (!pathsSet.has(filePath)) continue;
    
    const content = fileMatch[2];
    let importMatch;
    
    // Find imports in the file content
    while ((importMatch = importRegex.exec(content)) !== null) {
      const specifier = importMatch[1];
      
      // Skip external packages
      if (!specifier.startsWith('.') && !specifier.startsWith('@/')) continue;
      
      // Resolve the import path (simplified)
      const resolvedPath = resolveModulePath(filePath, specifier);
      if (resolvedPath && pathsSet.has(resolvedPath) && resolvedPath !== filePath) {
        dependencies.add(`${filePath}|${resolvedPath}`);
      }
    }
  }
  
  return Array.from(dependencies).map(dep => {
    const [source, target] = dep.split('|');
    return [source, target] as [string, string];
  });
}

/**
 * Resolve a module specifier to a file path
 */
function resolveModulePath(importerPath: string, specifier: string): string | null {
  // Handle @/ alias (common in React projects)
  if (specifier.startsWith('@/')) {
    return 'src/' + specifier.substring(2);
  }
  
  // Handle relative imports
  if (specifier.startsWith('.')) {
    const importerDir = importerPath.includes('/') 
      ? importerPath.substring(0, importerPath.lastIndexOf('/')) 
      : '';
      
    // Normalize path
    const resolved = normalizePath(importerDir + '/' + specifier);
    return resolved;
  }
  
  return null;
}

/**
 * Normalize a path by resolving '..' and '.'
 */
function normalizePath(path: string): string {
  const parts = path.split('/');
  const result = [];
  
  for (const part of parts) {
    if (part === '..') {
      result.pop();
    } else if (part !== '.' && part !== '') {
      result.push(part);
    }
  }
  
  return result.join('/');
}

/**
 * Generate Mermaid code from parsed data
 */
function generateMermaidFromParsedData(data: ParsedRepomixData): string {
  const lines = ["graph TD"];
  const nodeIds = new Map<string, string>();
  let idCounter = 0;
  
  // Function to get or create a Mermaid node ID
  const getNodeId = (path: string): string => {
    if (nodeIds.has(path)) return nodeIds.get(path)!;
    
    const id = `N${++idCounter}`;
    nodeIds.set(path, id);
    return id;
  };
  
  // Define nodes
  for (const node of data.nodes) {
    const id = getNodeId(node.id);
    const label = node.name.replace(/"/g, '\\"');
    
    if (node.type === 'dir') {
      lines.push(`    ${id}(("${label}"))`);
    } else {
      lines.push(`    ${id}["${label}"]`);
    }
  }
  
  // Add edges
  lines.push("\n    %% Structure Links");
  for (const edge of data.edges) {
    if (edge.type === 'structure') {
      const sourceId = getNodeId(edge.source);
      const targetId = getNodeId(edge.target);
      lines.push(`    ${sourceId} --- ${targetId}`);
    }
  }
  
  lines.push("\n    %% Dependency Links");
  for (const edge of data.edges) {
    if (edge.type === 'dependency') {
      const sourceId = getNodeId(edge.source);
      const targetId = getNodeId(edge.target);
      lines.push(`    ${sourceId} -.-> ${targetId}`);
    }
  }
  
  return lines.join("\n");
}

/**
 * Filter parsed data based on a filter object
 */
function filterParsedData(data: ParsedRepomixData, filter: Filter): ParsedRepomixData {
  const { filter_type, value, node_types = [], depth = 1 } = filter;
  
  // Initial matching nodes
  let matchedNodeIds = new Set<string>();
  
  // Apply primary filter
  if (filter_type === 'name_contains') {
    // Match nodes with names containing the value
    const valueLower = value.toLowerCase();
    data.nodes.forEach(node => {
      if (node.name.toLowerCase().includes(valueLower)) {
        matchedNodeIds.add(node.id);
      }
    });
  } 
  else if (filter_type === 'related_to_file') {
    // Find the specific file and its dependencies
    const targetNode = data.nodes.find(n => n.id === value || n.id.endsWith('/' + value));
    if (targetNode) {
      matchedNodeIds.add(targetNode.id);
      
      // Add directly related nodes (within specified depth)
      if (depth > 0) {
        let currentDepthNodes = new Set([targetNode.id]);
        let nextDepthNodes = new Set<string>();
        
        // BFS to add related nodes up to depth
        for (let d = 0; d < depth; d++) {
          for (const edge of data.edges) {
            if (currentDepthNodes.has(edge.source)) {
              nextDepthNodes.add(edge.target);
            }
            if (currentDepthNodes.has(edge.target)) {
              nextDepthNodes.add(edge.source);
            }
          }
          
          // Add found nodes to matched set
          nextDepthNodes.forEach(id => matchedNodeIds.add(id));
          
          // Prepare for next depth level
          currentDepthNodes = new Set(nextDepthNodes);
          nextDepthNodes = new Set();
        }
      }
    }
  }
  else if (filter_type === 'node_type') {
    // Match nodes by type
    if (value === 'file') {
      data.nodes
        .filter(n => n.type === 'file')
        .forEach(n => matchedNodeIds.add(n.id));
    }
    else if (value === 'dir' || value === 'directory') {
      data.nodes
        .filter(n => n.type === 'dir')
        .forEach(n => matchedNodeIds.add(n.id));
    }
    else if (value === 'hook') {
      data.nodes
        .filter(n => n.name.startsWith('use') && n.name.endsWith('.ts'))
        .forEach(n => matchedNodeIds.add(n.id));
    }
    else if (value === 'component') {
      data.nodes
        .filter(n => 
          n.id.includes('/components/') && 
          (n.name.endsWith('.tsx') || n.name.endsWith('.jsx'))
        )
        .forEach(n => matchedNodeIds.add(n.id));
    }
    else if (value === 'page') {
      data.nodes
        .filter(n => 
          (n.id.includes('/pages/') || n.id.includes('/screens/')) && 
          (n.name.endsWith('.tsx') || n.name.endsWith('.jsx'))
        )
        .forEach(n => matchedNodeIds.add(n.id));
    }
    else if (value === 'util' || value === 'utility') {
      data.nodes
        .filter(n => 
          (n.id.includes('/utils/') || n.id.includes('/helpers/')) && 
          n.type === 'file'
        )
        .forEach(n => matchedNodeIds.add(n.id));
    }
  }
  
  // Apply node type filtering if specified
  if (node_types && node_types.length > 0) {
    const filteredByType = new Set<string>();
    
    matchedNodeIds.forEach(id => {
      const node = data.nodes.find(n => n.id === id);
      if (!node) return;
      
      // Check if this node matches any of the requested types
      let matchesType = false;
      
      for (const type of node_types) {
        if (type === 'file' && node.type === 'file') {
          matchesType = true;
        }
        else if (type === 'dir' && node.type === 'dir') {
          matchesType = true;
        }
        else if (type === 'hook' && node.name.startsWith('use') && node.name.endsWith('.ts')) {
          matchesType = true;
        }
        else if (type === 'component' && 
                (node.id.includes('/components/') && 
                (node.name.endsWith('.tsx') || node.name.endsWith('.jsx')))) {
          matchesType = true;
        }
        else if (type === 'page' && 
                ((node.id.includes('/pages/') || node.id.includes('/screens/')) && 
                (node.name.endsWith('.tsx') || node.name.endsWith('.jsx')))) {
          matchesType = true;
        }
        else if ((type === 'util' || type === 'utility') && 
                ((node.id.includes('/utils/') || node.id.includes('/helpers/')) && 
                node.type === 'file')) {
          matchesType = true;
        }
      }
      
      if (matchesType) {
        filteredByType.add(id);
      }
    });
    
    // Update matched node IDs
    matchedNodeIds = filteredByType;
  }
  
  // Include parent nodes to maintain structure
  const allRequiredNodeIds = new Set(matchedNodeIds);
  matchedNodeIds.forEach(id => {
    const parts = id.split('/');
    for (let i = 1; i < parts.length; i++) {
      const parentPath = parts.slice(0, i).join('/');
      if (parentPath) allRequiredNodeIds.add(parentPath);
    }
  });
  
  // Filter nodes
  const filteredNodes = data.nodes.filter(node => allRequiredNodeIds.has(node.id));
  
  // Filter edges
  const filteredEdges = data.edges.filter(edge => 
    allRequiredNodeIds.has(edge.source) && allRequiredNodeIds.has(edge.target)
  );
  
  return {
    nodes: filteredNodes,
    edges: filteredEdges
  };
}

export default useRepomixToBeaUX;